import React, { useState } from 'react';
import { IconButton, Menu, MenuItem } from '@mui/material';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import { API_BASE_URL } from '../../config';
import {
  ArchiveCardAsset,
  ArchiveCardCategory,
  getArchiveCardPresentation,
} from './archiveCardText';

export interface ArchiveGridAsset extends ArchiveCardAsset {
  _id: string;
  filePath?: string;
  previewPaths?: {
    thumbnail?: string;
    medium?: string;
    videoPreview?: string;
  };
}

interface ArchiveAssetCardProps {
  asset: ArchiveGridAsset;
  categories: ArchiveCardCategory[];
  selected: boolean;
  viewing: boolean;
  disabled?: boolean;
  onOpen: () => void;
  onToggleSelect: () => void;
  onRestore: () => void;
  onDelete: () => void;
}

const thumbnailSrc = (asset: ArchiveGridAsset): string => {
  if (asset.previewPaths?.thumbnail) return `${API_BASE_URL}${asset.previewPaths.thumbnail}`;
  if ((asset.fileType || '').startsWith('image/') && asset.filePath) {
    return `${API_BASE_URL}/${asset.filePath.replace(/\\/g, '/')}`;
  }
  return '';
};

const fileGlyph = (asset: ArchiveGridAsset): string => {
  const fileType = asset.fileType || '';
  const fileName = (asset.fileName || '').toLowerCase();
  if (fileType.includes('pdf')) return '📄';
  if (fileType.startsWith('video/')) return '🎬';
  if (fileType.startsWith('audio/')) return '🎵';
  if (fileType.includes('postscript') || fileName.endsWith('.ai') || fileName.endsWith('.eps')) return '🎨';
  return '📁';
};

const ArchiveAssetCard: React.FC<ArchiveAssetCardProps> = ({
  asset,
  categories,
  selected,
  viewing,
  disabled = false,
  onOpen,
  onToggleSelect,
  onRestore,
  onDelete,
}) => {
  const [menuAnchor, setMenuAnchor] = useState<HTMLElement | null>(null);
  const presentation = getArchiveCardPresentation(asset, categories);
  const imageSrc = thumbnailSrc(asset);
  const border = selected ? '2px solid #072f53' : viewing ? '2px solid #8aa4be' : '1px solid #d4d4d4';

  return (
    <div
      data-asset-card
      data-asset-id={asset._id}
      onClick={onOpen}
      style={{
        display: 'flex',
        flexDirection: 'column',
        border,
        borderRadius: '8px',
        cursor: 'pointer',
        backgroundColor: 'white',
        boxShadow: selected ? '0 0 8px rgba(7, 47, 83, 0.5)' : 'none',
        position: 'relative',
        overflow: 'hidden',
        height: '100%',
      }}
    >
      <input
        type="checkbox"
        aria-label={`Markera ${presentation.title}`}
        checked={selected}
        disabled={disabled}
        onClick={(event) => event.stopPropagation()}
        onChange={onToggleSelect}
        style={{
          position: 'absolute',
          top: 8,
          left: 8,
          zIndex: 2,
          width: 18,
          height: 18,
          margin: 0,
          cursor: 'pointer',
        }}
      />
      <IconButton
        size="small"
        aria-label={`Åtgärder för ${presentation.title}`}
        disabled={disabled}
        onClick={(event) => {
          event.stopPropagation();
          setMenuAnchor(event.currentTarget);
        }}
        sx={{
          position: 'absolute',
          top: 8,
          right: 8,
          zIndex: 2,
          backgroundColor: 'rgba(255, 255, 255, 0.8)',
          '&:hover': { backgroundColor: 'rgba(255, 255, 255, 0.95)' },
        }}
      >
        <MoreVertIcon fontSize="small" />
      </IconButton>
      <Menu
        anchorEl={menuAnchor}
        open={Boolean(menuAnchor)}
        onClose={() => setMenuAnchor(null)}
        onClick={(event) => event.stopPropagation()}
      >
        <MenuItem
          onClick={() => {
            setMenuAnchor(null);
            onRestore();
          }}
        >
          Återställ
        </MenuItem>
        <MenuItem
          onClick={() => {
            setMenuAnchor(null);
            onDelete();
          }}
          sx={{ color: 'error.main' }}
        >
          Radera
        </MenuItem>
      </Menu>

      <div
        style={{
          height: 180,
          backgroundColor: '#e9ecef',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          overflow: 'hidden',
          padding: 8,
        }}
      >
        {imageSrc ? (
          <img
            src={imageSrc}
            alt={presentation.title}
            loading="lazy"
            style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }}
          />
        ) : (
          <div style={{ textAlign: 'center', color: '#666' }}>
            <div style={{ fontSize: 36, lineHeight: 1.2 }}>{fileGlyph(asset)}</div>
            <div style={{ fontSize: 12, marginTop: 4 }}>{presentation.metaLine || asset.fileType || 'Fil'}</div>
          </div>
        )}
      </div>

      <div style={{ padding: '8px 10px 12px', textAlign: 'center' }}>
        <p
          title={presentation.title}
          style={{
            margin: '0 0 4px',
            fontWeight: 'bold',
            fontSize: 13,
            lineHeight: 1.15,
            overflow: 'hidden',
            display: presentation.isRecipe ? '-webkit-box' : 'block',
            WebkitLineClamp: presentation.isRecipe ? 2 : undefined,
            WebkitBoxOrient: presentation.isRecipe ? 'vertical' : undefined,
            whiteSpace: presentation.isRecipe ? 'normal' : 'nowrap',
            textOverflow: presentation.isRecipe ? undefined : 'ellipsis',
          }}
        >
          {presentation.title}
        </p>
        {presentation.metaLine && imageSrc ? (
          <div style={{ fontSize: 12, color: '#888' }}>{presentation.metaLine}</div>
        ) : null}
        {presentation.categoryLabels.length > 0 && (
          <div style={{ display: 'flex', justifyContent: 'center', flexWrap: 'wrap', gap: 4, marginTop: 4 }}>
            {presentation.categoryLabels.map(label => (
              <span
                key={label.id}
                style={{ backgroundColor: '#f0f0f0', padding: '2px 6px', borderRadius: 4, fontSize: 11, color: '#666' }}
              >
                {label.name}
              </span>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default ArchiveAssetCard;
